'use client'

// Inline attachment strip for the editor body: image thumbnails with a remove
// × only. All management (upload, progress, restore) lives in the shared
// Attachments modal. Renders nothing when there is nothing to show — the
// upload input stays mounted inside the modal, not here.

import type { MouseEvent } from 'react'
import { classNames } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import type { EditorAttachmentState } from '@/msgdeskRuntime/state'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { useEditorAttachments, visibleEditorAttachments } from '../../useEditorAttachments'
import { useEditorAttachmentsUiSkin } from '../Modal/AttachmentsModal'

function removeAttachment(event: MouseEvent<HTMLElement>, attachment: EditorAttachmentState) {
  if (!attachment.id) return
  event.preventDefault()
  event.stopPropagation()
  dispatchEditorAction({ action: 'toggle-attachment', attachment, active: false })
}

export function EditorAttachmentsStrip({ editorMode }: { editorMode?: EditorMode }) {
  const ui = useEditorAttachmentsUiSkin()
  const attachments = visibleEditorAttachments(useEditorAttachments(), editorMode)
    .filter(attachment => attachment.active !== false && attachment.src)

  if (!attachments.length) return null

  return (
    <div className={classNames('md-attachments-strip', ui.strip)}>
      {attachments.map((attachment, index) => (
        <span key={attachment.id || `strip-${index}`} className={classNames('md-attachments-strip__thumb', ui.stripThumb)}>
          <img
            className={classNames('md-attachments-strip__img', ui.stripImg)}
            src={attachment.src}
            alt={attachment.filename || ''}
            title={attachment.filename || ''}
          />
          {attachment.removable ? (
            <button
              type="button"
              className={classNames('md-attachments-strip__remove', ui.stripRemove)}
              onClick={event => removeAttachment(event, attachment)}
              title="Remove attachment"
              aria-label="Remove attachment"
            >
              ×
            </button>
          ) : null}
        </span>
      ))}
    </div>
  )
}
